<template>
  <nav
    v-show="!isOverlayOpen"
    class="bg-gradient-to-r from-background-07 to-background-coral px-6 py-2.5 lg:py-6"
    aria-label="Hoofdnavigatie"
  >
    <div class="mx-auto flex max-w-[80rem] items-center justify-between">
      <div class="flex items-center gap-14">
        <a href="/" class="inline-block">
          <NavigationLogo class="hidden lg:block"></NavigationLogo>
          <NavigationLogo class="lg:hidden" variant="transparent"></NavigationLogo>
          <span class="sr-only">Startpagina</span>
        </a>

        <ul class="hidden items-center gap-8 lg:flex">
          <li v-for="item in computedNavItems" :key="item.name">
            <NavDropdown
              v-if="item.hasChildren && item.isActive"
              :model-value="getActiveOption(item.name)"
              :placeholder="item.name"
              :options="item.children"
              :active-option="getActiveOption(item.name)"
            />

            <template v-else>
              <NavDropdown
                v-if="item.hasChildren"
                :placeholder="item.name"
                :options="item.children"
              />

              <NavLink
                v-else
                :text="item.name"
                :url="item.url"
                :is-active="item.isActive"
              ></NavLink>
            </template>
          </li>
        </ul>
      </div>

      <div class="flex items-center gap-1">
        <a
          :href="loginLink.url"
          :target="loginLink.target"
          class="rounded-full bg-white px-3.5 py-1.5 text-xs font-bold text-text-dark no-underline lg:px-6 lg:py-2.5 lg:text-sm"
          aria-label="Inloggen"
          >{{ loginLink.text }}</a
        >

        <button class="p-2.5 lg:hidden" aria-label="Toggle menu" @click="toggleOverlay">
          <BaseIcon class="size-6 text-white" :icon="'Menu'"></BaseIcon>
        </button>
      </div>
    </div>
  </nav>

  <NavMobileOverlay v-show="isOverlayOpen" :nav-items="computedNavItems" @close="toggleOverlay">
  </NavMobileOverlay>
</template>

<script setup lang="ts">
import NavigationLogo from '@/features/navigation/components/NavigationLogo/NavigationLogo.vue'
import NavDropdown from '@/features/navigation/components/NavDropdown/NavDropdown.vue'
import NavLink from '@/features/navigation/components/NavLink/NavLink.vue'
import type { INavDropdownOptions } from '@/features/navigation/components/NavDropdown/INavDropdown.ts'
import BaseIcon from '@/common/components/base/BaseIcon/BaseIcon.vue'
import { computed, ref, watch } from 'vue'
import NavMobileOverlay from '@/features/navigation/components/NavMobileOverlay/NavMobileOverlay.vue'
import type { INavigation, INavItemWithDropdown } from '@/features/navigation/types/INavigation.ts'

const props = defineProps<INavigation>()

const getActiveOption = (name: string) => {
  const activeItem = props.navItems.find(item => {
    if (item.name !== name) return undefined

    if (item.hasChildren && item.isActive) {
      return item.children?.some(link => link.isActive)
    } else {
      return item.isActive
    }
  })

  if (!activeItem) return undefined

  if (activeItem.hasChildren && activeItem.children) {
    const activeChild = activeItem.children.find(link => link.isActive)
    if (activeChild) {
      return {
        label: activeChild.name,
        value: activeChild.url,
      }
    }
  }

  return {
    label: activeItem.name,
    value: activeItem.url,
  } as INavDropdownOptions
}

const computedNavItems = computed(() => {
  return props.navItems.map(item => {
    return {
      name: item.name,
      url: item.url,
      hasChildren: item.hasChildren,
      children: item.children?.map(child => {
        return {
          value: child.url,
          label: child.name,
        }
      }),
      isActive: item.isActive,
    } as INavItemWithDropdown
  })
})

const isOverlayOpen = ref(false)

const toggleOverlay = () => {
  isOverlayOpen.value = !isOverlayOpen.value
}

//#region no scroll body
watch(isOverlayOpen, (value: boolean) => {
  if (value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
//#endregion
</script>
